'use client';

import { useCallback, useEffect } from 'react';
import {
  DEFAULT_LISTING_ACTION_ANALYTICS,
  type ListingActionAnalyticsContext,
  type ListingLike,
} from './listingActionUtils';
import {
  type ListingActionListing,
  type ListingActionOpenOptions,
  useListingActionContext,
} from './ListingActionProvider';

export type ListingActions = {
  listingSaved: boolean;
  listingHidden: boolean;
  toggleSave: () => void;
  toggleHide: () => void;
  openContact: (options?: ListingActionOpenOptions) => void;
  openShare: () => void;
  openReport: () => void;
};

export function useListingActions(
  listing: ListingActionListing | null | undefined,
  analyticsContext: ListingActionAnalyticsContext = DEFAULT_LISTING_ACTION_ANALYTICS,
): ListingActions {
  const {
    openContact,
    openShare,
    openReport,
    isListingSaved,
    isListingHidden,
    reconcileFavoriteOverride,
    toggleSaveListing,
    toggleHideListing,
  } = useListingActionContext();

  const listingSaved = isListingSaved(listing as ListingLike | null | undefined);
  const listingHidden = isListingHidden(listing as ListingLike | null | undefined);

  useEffect(() => {
    reconcileFavoriteOverride(listing as ListingLike | null | undefined);
  }, [listing, reconcileFavoriteOverride]);

  const toggleSave = useCallback(() => {
    void toggleSaveListing(listing as ListingLike | null | undefined, analyticsContext);
  }, [analyticsContext, listing, toggleSaveListing]);

  const toggleHide = useCallback(() => {
    toggleHideListing(listing as ListingLike | null | undefined, analyticsContext);
  }, [analyticsContext, listing, toggleHideListing]);

  const handleOpenContact = useCallback(
    (options?: ListingActionOpenOptions) => {
      void openContact(listing, { ...analyticsContext, ...options });
    },
    [analyticsContext, listing, openContact],
  );

  const handleOpenShare = useCallback(() => {
    openShare(listing);
  }, [listing, openShare]);

  const handleOpenReport = useCallback(() => {
    openReport(listing, analyticsContext);
  }, [analyticsContext, listing, openReport]);

  return {
    listingSaved,
    listingHidden,
    toggleSave,
    toggleHide,
    openContact: handleOpenContact,
    openShare: handleOpenShare,
    openReport: handleOpenReport,
  };
}
